import { createElement, lazy, Suspense } from 'react';
import { usesDesignV2 } from '../features/design-v2/rollout';
import { flowEditableName, flowBrand } from '../api/core';
import type { PageContext } from '../lib/types';
import { html, raw, toast } from '../lib/dom';
import { setQuery } from '../lib/router';
import * as store from '../store/ui';
import { db, loadTable, refreshTable } from '../api/core';
import { listFlows, createBlankFlow, renameFlow, duplicateFlow } from '../api/flow-directory';
import { getFlow, deleteFlow } from '../api/flows';
import { getProject, getWorkspace, getUser, listProjects, listWorkspaces } from '../api/workspace';
import { coverThumb } from '../components/thumb';
import { flowTemplatePreview } from '../components/flow-template-preview';
import { coverEditor, bindCoverEditors } from '../components/entity-cover';
import { pageHead, tabs, select, searchInput, status, table, favButton, empty } from '../components/ui';
import { openModal } from '../components/modal';
import { icon } from '../components/icons';
import { flowTemplates } from '../../../../packages/canvas-runtime/flow-templates.mjs';
import './flow-create.css';
import { relTime, plural } from '../lib/format';

const FlowsPage = lazy(() => import('../features/design-v2/FlowsPage').then(module => ({ default: module.FlowsPage })));

const VIEWS = [['all', 'All'], ['recent', 'Recent'], ['favorites', 'Favorites'], ['mine', 'Mine']];
// Only the latest run of each flow is shown, so the full run history is not loaded here.
const TABLES = ['flows', 'projects', 'brands', 'workspaces', 'flow_last_runs', 'users'] as const;
let loadingState: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
let loadError = '';
const flowHref = (flow: any) => `#/projects/${flow.project_id}/flows/${flow.id}`;

function actions(flow: any) {
  return html`<div class="flow-actions">${favButton('flow', flow.id)}<button class="icon-btn" data-menu-toggle="flow-${flow.id}" aria-label="Actions for ${flow.name}" aria-haspopup="menu">${icon('more')}</button>
    <div class="menu right" data-menu="flow-${flow.id}" hidden>
      <a class="menu-item" href="${flowHref(flow)}">${icon('play')}Open canvas</a>
      <button class="menu-item" data-flow-action="rename" data-flow-id="${flow.id}">Rename</button>
      <button class="menu-item" data-flow-action="cover" data-flow-id="${flow.id}">${icon('gallery')}Change image</button>
      <button class="menu-item" data-flow-action="duplicate" data-flow-id="${flow.id}">${icon('copy')}Duplicate</button>
      <button class="menu-item err" data-flow-action="delete" data-flow-id="${flow.id}">${icon('trash')}Delete</button>
    </div></div>`;
}

function lastRun(flow: any, run?: any) {
  return run ? html`<a class="flow-last-run" href="#/runs/${run.id}" title="View last run">${status(run.status)}<span class="mono muted">${relTime(run.created_at)}</span></a>` : html`<span class="mono muted">Not run yet</span>`;
}

function openCreate(ctx: PageContext) {
  openModal({
    title: 'New flow', wide: true,
    body: html`<form class="form" data-create-flow>
      <div class="field"><label for="flow-name">Name</label><input id="flow-name" class="input" name="name" maxlength="255" placeholder="e.g. brand assets" required autofocus></div>
      <div class="field"><label for="flow-project">Project</label><select id="flow-project" class="select" name="project" required><option value="">Choose a project</option>${listProjects().map(p => html`<option value="${p.id}" ${raw(p.id === ctx.query.project ? 'selected' : '')}>${p.name}</option>`)}</select></div>
      <fieldset class="flow-template-picker"><legend>Template</legend><div class="flow-template-grid">
        ${[{id:'',name:'Blank flow',description:'Start with an empty canvas',hue:90},...flowTemplates].map(t => html`<label class="card entity-card flow-template-choice">
          <input type="radio" name="template" value="${t.id}" aria-label="${t.name}" ${raw(t.id ? '' : 'checked')}>
          ${flowTemplatePreview(t.id)}<div class="body"><span class="title">${t.name}</span><span class="meta">${t.description}</span></div>
        </label>`)}
      </div></fieldset>
      <p class="muted">Enter the flow name without a brand. The project brand is added automatically.</p>
      <p class="banner error" role="alert" data-error hidden></p>
    </form>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-create>Create flow</button>`,
    onMount(modal: HTMLElement, close: () => void) {
      const form = modal.querySelector<HTMLFormElement>('form')!;
      const template = form.elements.namedItem('template') as RadioNodeList;
      form.addEventListener('change', event => {
        if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'template') return;
        const selected = flowTemplates.find(t => t.id === template.value);
        const name = form.elements.namedItem('name') as HTMLInputElement;
        if (!name.value || flowTemplates.some(t => t.name === name.value)) name.value = selected?.name || '';
      });
      const project = form.elements.namedItem('project') as HTMLSelectElement;
      const button = modal.querySelector<HTMLButtonElement>('[data-create]')!;
      let pending = false;
      const submit = async (event: Event) => {
        event.preventDefault();
        if (pending || !form.reportValidity()) return;
        const error = modal.querySelector<HTMLElement>('[data-error]')!;
        const name = (form.elements.namedItem('name') as HTMLInputElement).value;
        pending = true; button.disabled = true; button.textContent = 'Creating…'; error.hidden = true;
        try {
          const flow = await createBlankFlow({ projectId: project.value, name, templateId:template.value });
          close(); location.hash = flowHref(flow).slice(1);
        } catch (cause: any) { error.textContent = cause.message || 'Could not create flow.'; error.hidden = false; }
        finally { pending = false; button.disabled = false; button.textContent = 'Create flow'; }
      };
      form.addEventListener('submit', submit); button.addEventListener('click', submit);
    },
  });
}

function editModal(flow: any, action: string, ctx: PageContext) {
  const removing = action === 'delete';
  openModal({ title: removing ? `Delete ${flow.name}?` : 'Rename flow',
    body: removing ? html`<p>This deletes the flow, its saved graph, version history and runs. This cannot be undone.</p><p class="banner error" role="alert" data-error hidden></p>`
      : html`<form class="form"><div class="field"><label for="rename-flow">Name</label><span class="muted">${flowBrand(flow)?.name ? `${flowBrand(flow)?.name} - ` : 'No project brand'}</span><input id="rename-flow" class="input" name="name" value="${flowEditableName(flow)}" maxlength="255" required autofocus></div><p class="banner error" role="alert" data-error hidden></p></form>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn ${removing ? 'danger' : 'primary'}" data-save>${removing ? 'Delete flow' : 'Save'}</button>`,
    onMount(modal: HTMLElement, close: () => void) {
      const button = modal.querySelector<HTMLButtonElement>('[data-save]')!;
      const form = modal.querySelector<HTMLFormElement>('form');
      const save = async (event: Event) => {
        event.preventDefault();
        if (button.disabled || (form && !form.reportValidity())) return;
        button.disabled = true;
        const error = modal.querySelector<HTMLElement>('[data-error]')!; error.hidden = true;
        try {
          if (removing) await deleteFlow(flow.id);
          else await renameFlow(flow.id, (form!.elements.namedItem('name') as HTMLInputElement).value);
          close(); toast(removing ? 'Flow deleted' : 'Flow renamed'); ctx.rerender();
        } catch (cause: any) { error.textContent = cause.message; error.hidden = false; }
        finally { button.disabled = false; }
      };
      button.addEventListener('click', save); form?.addEventListener('submit', save);
    },
  });
}

export function render(ctx: PageContext) {
  if (usesDesignV2(ctx)) return {
    title: 'Flows',
    content: createElement(Suspense, { fallback: createElement('div', { role: 'status', className: 'page' }, 'Loading flows…') }, createElement(FlowsPage, { context: ctx, onCreate: () => openCreate(ctx) })),
  };
  const q = ctx.query;
  const state = store.get();
  const view = VIEWS.some(([key]) => key === q.view) ? q.view : 'all';
  const workspaceId = ctx.workspaceId || q.workspace || null;
  const filters = { workspaceId, projectId: q.project, q: q.q, sort: q.sort, favorites: state.favorites, recents: state.recents };
  const rows = listFlows({ ...filters, view });
  const counts = VIEWS.map(([key, label]) => [key, label, listFlows({ ...filters, view: key }).length]);
  const runByFlow = new Map<string, any>(db.flow_last_runs.map(run => [run.flow_id, run]));
  const hasProjects = listProjects({ workspaceId }).length > 0;
  const isReady = loadingState === 'ready';
  const clear = html`<button class="btn sm" data-clear-flow-filters>Clear filters</button>`;
  const body = html`<div class="page flows-page">
    ${pageHead({ title: 'Flows', lede: 'Find your workflows across projects and open them in Canvas.', actions: html`<button class="btn primary" data-new-flow ${raw(!isReady || !hasProjects ? 'disabled' : '')}>${icon('plus')}New flow</button>` })}
    ${tabs(counts, view, { attr: 'data-view' })}
    <div class="toolbar">
      ${searchInput(q.q, 'Search flows or projects…')}
      ${ctx.workspaceId ? html`<span class="chip">${getWorkspace(ctx.workspaceId)?.name}</span>` : select('workspace', listWorkspaces().map(w => [w.id, w.name]), q.workspace, { placeholder: 'Any workspace' })}
      ${select('project', listProjects({ workspaceId }).map(p => [p.id, p.name]), q.project, { placeholder: 'Any project' })}
      ${q.q || q.workspace || q.project ? clear : ''}<span class="spacer"></span>
      ${select('sort', [['updated', 'Recently updated'], ['created', 'Recently created'], ['name', 'Name']], q.sort || 'updated', { placeholder: 'Sort' })}
      <div class="seg" role="group" aria-label="Layout"><button class="${q.layout !== 'table' ? 'on' : ''}" data-param="layout" data-value="grid" aria-label="Card view">${icon('grid')}</button><button class="${q.layout === 'table' ? 'on' : ''}" data-param="layout" data-value="table" aria-label="Table view">${icon('list')}</button></div>
    </div>
    ${loadingState === 'error' ? html`<div class="banner error" role="alert">${loadError}<button class="btn sm" data-retry>Retry</button></div>`
      : !isReady ? empty('Loading flows…')
      : !rows.length ? empty(view === 'favorites' ? 'Star a flow to find it here.' : view === 'recent' ? 'Open a flow in Canvas and it will appear here.' : view === 'mine' ? 'Flows you create will appear here.' : 'No flows found.', hasProjects ? html`${clear}<button class="btn sm primary" data-new-flow>New flow</button>` : html`<a class="btn" href="#/projects?new=1">Create a project</a>`)
      : html`<div class="mono muted flow-count">${plural(rows.length, 'flow')}</div>${q.layout === 'table' ? html`<div class="card">${table([
        { label: 'Flow', render: (f: any) => html`<div class="cell-entity">${coverThumb(f, 'thumb flow-table-cover')}<a class="ellipsis" href="${flowHref(f)}" title="${f.name}">${f.name}</a></div>` },
        { label: 'Project', render: (f: any) => html`<a href="#/projects/${f.project_id}">${getProject(f.project_id)?.name}</a>` },
        { label: 'Workspace', render: (f: any) => getWorkspace(getProject(f.project_id)?.workspace_id)?.name },
        { label: 'Version', render: (f: any) => status(f.published_version ? 'published' : 'draft', f.published_version ? `Published v${f.published_version}` : 'Draft') },
        { label: 'Nodes', num: true, render: (f: any) => f.node_count ?? f.graph?.nodes?.length ?? 0 },
        { label: 'Last run', render: (f: any) => lastRun(f, runByFlow.get(f.id)) },
        { label: 'Creator', render: (f: any) => f.created_by ? getUser(f.created_by).name : '—' },
        { label: 'Updated', render: (f: any) => relTime(f.updated_at) },
        { label: '', render: actions },
      ], rows, { rowHref: flowHref })}</div>` : html`<div class="flow-grid">${rows.map(flow => {
        const project = getProject(flow.project_id);
        const run = runByFlow.get(flow.id);
        return html`<article class="card flow-card">
          <a class="flow-cover-link" href="${flowHref(flow)}" aria-label="Open ${flow.name}">${coverThumb(flow, 'thumb flow-cover')}</a>
          <div class="flow-card-body">
            <div class="row"><a class="flow-title ellipsis" href="${flowHref(flow)}" title="${flow.name}">${flow.name}</a>${actions(flow)}</div>
            <div class="flow-context"><a class="ellipsis" href="#/projects/${project?.id}" title="${project?.name}">${project?.name}</a><span class="ellipsis" title="${getWorkspace(project?.workspace_id)?.name}">${getWorkspace(project?.workspace_id)?.name}</span></div>
            <div class="row wrap">${status(flow.published_version ? 'published' : 'draft', flow.published_version ? `Published v${flow.published_version}` : 'Draft')}<span class="mono muted">${plural(flow.node_count ?? flow.graph?.nodes?.length ?? 0, 'node')}</span></div>
            <div class="flow-run-line">${lastRun(flow, run)}</div>
            <div class="flow-card-footer"><span class="ellipsis" title="${flow.created_by ? getUser(flow.created_by).name : ''}">${flow.created_by ? getUser(flow.created_by).name : 'Creator unavailable'}</span><span>Updated ${relTime(flow.updated_at)}</span></div>
          </div>
        </article>`;
      })}</div>`}`}
  </div>`;
  return { title: 'Flows', body, mount(root: HTMLElement) {
    const listeners = new AbortController();
    const listenerOptions = { signal: listeners.signal };
    const load = async () => {
      loadingState = 'loading';
      try { await Promise.all(TABLES.map(t => loadTable(t))); loadingState = 'ready'; }
      catch (cause: any) { loadingState = 'error'; loadError = cause.message || 'Could not load flows.'; }
      ctx.rerender();
    };
    if (loadingState === 'idle') void load();
    root.querySelector('[data-retry]')?.addEventListener('click', () => { void load(); }, listenerOptions);
    root.querySelectorAll('[data-new-flow]').forEach(button => button.addEventListener('click', () => openCreate(ctx), listenerOptions));
    root.querySelector<HTMLSelectElement>('[data-filter="workspace"]')?.addEventListener('change', () => setQuery({ project: null }), listenerOptions);
    root.querySelectorAll('[data-clear-flow-filters]').forEach(button => button.addEventListener('click', () => { setQuery({ q: null, workspace: null, project: null, view: 'all' }); ctx.rerender(); }, listenerOptions));
    const actionClick = async (event: Event) => {
      const toggle = (event.target as HTMLElement).closest<HTMLElement>('.flow-actions [data-menu-toggle]');
      if (toggle) {
        const menu = toggle.parentElement!.querySelector<HTMLElement>('[data-menu]')!;
        const anchor = toggle.getBoundingClientRect();
        // Place menus outside the scrolling table so every action remains accessible.
        Object.assign(menu.style, { position: 'fixed', right: 'auto', left: `${Math.max(12, anchor.right - 190)}px`, top: `${anchor.bottom + 6}px` });
        requestAnimationFrame(() => {
          if (menu.hidden || !menu.isConnected) return;
          const size = menu.getBoundingClientRect();
          menu.style.left = `${Math.max(12, Math.min(anchor.right - size.width, window.innerWidth - size.width - 12))}px`;
          menu.style.top = `${Math.max(12, anchor.bottom + size.height + 12 > window.innerHeight ? anchor.top - size.height - 6 : anchor.bottom + 6)}px`;
        });
        return;
      }
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-flow-action]');
      if (!button || button.disabled) return;
      const flow = getFlow(button.dataset.flowId); if (!flow) return;
      const action = button.dataset.flowAction;
      root.querySelectorAll<HTMLElement>('[data-menu]').forEach(menu => { menu.hidden = true; });
      if (action === 'cover') {
        openModal({ title: `Image for ${flow.name}`, body: coverEditor(flow, 'flow'), onMount: (modal: HTMLElement) => bindCoverEditors(modal, ctx.rerender) });
      } else if (action === 'rename' || action === 'delete') editModal(flow, action, ctx);
      else if (action === 'duplicate') {
        button.disabled = true;
        try { const copy = await duplicateFlow(flow.id); toast('Flow duplicated'); location.hash = flowHref(copy).slice(1); }
        catch (cause: any) { toast(cause.message || 'Could not duplicate flow.'); }
        finally { button.disabled = false; }
      }
    };
    root.addEventListener('click', actionClick);
    const closeMenus = () => root.querySelectorAll<HTMLElement>('.flow-actions [data-menu]').forEach(menu => { menu.hidden = true; });
    root.addEventListener('scroll', closeMenus, { capture: true, signal: listeners.signal });
    window.addEventListener('resize', closeMenus, listenerOptions);
    const poll = isReady && db.flow_last_runs.some(run => ['running', 'queued'].includes(run.status))
      ? window.setInterval(() => { void refreshTable('flow_last_runs').catch((): void => undefined); }, 15_000) : null;
    return () => { listeners.abort(); root.removeEventListener('click', actionClick); if (poll !== null) window.clearInterval(poll); };
  } };
}
